/**
 * The push layer's web twin. The resolver picks this file over `push.ts` for
 * the web build - see `vite.config.mjs` - so nothing here may import
 * Firebase or notifee, which is the entire point of the file.
 *
 * ---------------------------------------------------------------------------
 * WHAT "NO PUSH" COSTS ON THE WEB, HONESTLY
 * ---------------------------------------------------------------------------
 * A browser tab cannot be rung while it is closed. Web push (a service
 * worker + the Firebase JS SDK) could one day narrow that, but it is real
 * work with its own permission prompt, and it is not this change.
 *
 * What still works, because it never depended on push: the provider's own
 * in-app polling finds an incoming call while the tab is open, raises the
 * same `IncomingCallOverlay`, and the whole call then runs over the
 * browser's native WebRTC. So calls on the web behave like calls in the
 * foregrounded app - which is every call a person looking at the tab can
 * answer anyway.
 *
 * Every export keeps its native signature so `VoiceCallProvider` and
 * `HomeScreen` compile against either twin unchanged.
 */

import type { VoiceIncomingPush, VoicePushEvent } from './incoming-call';

/** No token to register: there is no FCM in this build. Resolves quietly. */
export const registerPushToken = async (): Promise<void> => {};

/** Nothing was registered, so there is nothing to forget. */
export const forgetPushToken = (): void => {};

export type VoicePushHandlers = {
  /** A call is ringing this handset. Carries whatever the push knew. */
  onIncoming: (push: VoiceIncomingPush) => void;
  /** A call this handset cares about reached a terminal state. */
  onEnded: (callId: string | null, event: VoicePushEvent) => void;
  /** The Accept BUTTON was pressed. This answers the call. */
  onAcceptPressed: (callId: string) => void;
  /** Decline was pressed on the notification. */
  onDeclinePressed: (callId: string) => void;
  /** The card was tapped, or the app was launched by one. */
  onOpenPressed: (callId: string) => void;
};

/**
 * No push, no doors to wire. The unsubscribe is as much of a no-op as the
 * subscription was.
 */
export const subscribeVoicePush = (
  _handlers: VoicePushHandlers,
): (() => void) => () => {};

/**
 * The queue this drains is written by notification Decline buttons, which do
 * not exist here - so it is empty by construction.
 */
export const drainQueuedDecline = async (
  _reject: (callId: string) => Promise<void>,
): Promise<void> => {};

/* Same one-import surface as the native twin. Resolves to
   `incoming-call.web.ts` at runtime, to `incoming-call.ts` in the editor -
   both export these names with the same values and types. */
export { queueDecline, VOICE_ACTION_ACCEPT, VOICE_ACTION_DECLINE } from './incoming-call';
export type { VoiceIncomingPush, VoicePushEvent };
